import { createSlice, original } from '@reduxjs/toolkit';
import { months, captureRowValues } from '../utils/budgetHierarchyUtils';
import { isSameAmount } from '../utils/budgetValueUtils';
import { STATUSES } from '../constants/statuses';
import {
    MAX_EDIT_HISTORY, snapshotRow, pushEditHistory, findRowIndex, markRowDirty, addCellHistory, syncParentTotals
} from './budgetReducerHelpers';
import { fetchBudgetData } from './budgetLoadThunk';
import {
    saveBudgetData, changeBudgetStatus, fetchVersionHistory, restoreBudgetVersion, fetchEurostatData, fetchRowHistory
} from './budgetThunks';

export { fetchBudgetData } from './budgetLoadThunk';
export {
    saveBudgetData, changeBudgetStatus, fetchVersionHistory, restoreBudgetVersion, fetchEurostatData, fetchRowHistory
} from './budgetThunks';
export * from './budgetSelectors';
export { isBudgetValueInvalid, findBudgetViolations } from '../utils/budgetValidation';

const initialState = {
    budgetData: [],
    historyData: {},
    cellHistory: {},
    territoryStatuses: {},
    territoryComments: {},
    budgetDocumentIds: {},
    eurostatData: {},
    activeTerritoryId: null,
    versions: [],
    versionLoading: false,
    loading: false,
    isInitialized: false,
    showVersionHistory: false,
    error: null,
    year: '',
    budgetType: 'INTERNAL',
    isDirty: false,
    dirtyRowIds: [],
    invalidRowIds: [],
    focusRowRequest: null,
    filteredRowIds: null,
    editHistory: [],
    hiddenBudgetLines: [],
    isTooltipEnabled: false,
    isEcoEnabled: false,
    isEditMode: false,
    displayedRowCount: 0,
    selectedRow: null,
};

const budgetSlice = createSlice({
    name: 'salesBudget',
    initialState,
    reducers: {
        setYear: (state, action) => {
            state.year = action.payload;
            state.isInitialized = false;
            state.isDirty = false;
            state.dirtyRowIds = [];
            state.invalidRowIds = [];
            state.editHistory = [];
            state.isEditMode = false;
        },
        setBudgetType: (state, action) => {
            state.budgetType = action.payload;
            state.isInitialized = false;
            state.isDirty = false;
            state.dirtyRowIds = [];
            state.invalidRowIds = [];
            state.editHistory = [];
            state.historyData = {};
            state.isEditMode = false;
        },
        setActiveTerritory: (state, action) => {
            state.activeTerritoryId = action.payload;
        },
        toggleVersionHistory: (state) => {
            state.showVersionHistory = !state.showVersionHistory;
        },
        toggleTooltip: (state) => {
            state.isTooltipEnabled = !state.isTooltipEnabled;
        },
        toggleEditMode: (state) => {
            state.isEditMode = !state.isEditMode;
        },
        setDisplayedRowCount: (state, action) => {
            state.displayedRowCount = action.payload;
        },
        toggleEco: (state) => {
            state.isEcoEnabled = !state.isEcoEnabled;
            state.isInitialized = false;
        },
        setSelectedRow: (state, action) => {
            state.selectedRow = action.payload;
        },
        requestFocusRow: (state, action) => {
            state.focusRowRequest = { id: String(action.payload), requestId: (state.focusRowRequest?.requestId || 0) + 1 };
        },
        clearInvalidRowIds: (state) => {
            state.invalidRowIds = [];
        },
        setFilteredRowIds: (state, action) => {
            state.filteredRowIds = action.payload;
        },
        undoLastEdit: (state) => {
            const entry = (state.editHistory || []).pop();
            if (!entry) return;

            const previousById = new Map(entry.rows.map(r => [r.id, r.values]));
            const rows = original(state.budgetData);
            const changes = [];
            const touchedTerritories = new Set();
            const dirtySet = new Set(state.dirtyRowIds.map(String));

            rows.forEach((plainRow, index) => {
                const id = String(plainRow.id);
                const previous = previousById.get(id);
                if (!previous) return;

                const row = state.budgetData[index];
                const before = captureRowValues(row);
                months.forEach(m => { row[m] = previous[m]; });
                row.total = months.reduce((sum, m) => sum + (Number(row[m]) || 0), 0);
                changes.push({ row, before });
                touchedTerritories.add(String(plainRow.territoryId));

                const backToSaved = months.every(m => isSameAmount(row[m], plainRow[`base_${m}`]));
                if (backToSaved) dirtySet.delete(id);
                else dirtySet.add(id);
            });

            touchedTerritories.forEach(tId => {
                const territoryHasDirty = rows.some(r =>
                    r.type === 'PRODUCT' && String(r.territoryId) === tId && dirtySet.has(String(r.id))
                );
                if (territoryHasDirty) dirtySet.add(tId);
                else dirtySet.delete(tId);
            });

            state.dirtyRowIds = Array.from(dirtySet);
            state.isDirty = state.dirtyRowIds.length > 0;
            state.invalidRowIds = state.invalidRowIds.filter(id => !previousById.has(String(id)));
            syncParentTotals(state, changes);
        },
        copyFromReference: (state, action) => {
            const territoryId = action.payload;
            if (!territoryId) return;
            const targetHierarchy = state.selectedRow ? state.selectedRow.hierarchy : null;
            const rows = original(state.budgetData);
            const rowsBeforeCopy = [];
            const changes = [];

            rows.forEach((plainRow, index) => {
                if (plainRow.type !== 'PRODUCT' || String(plainRow.territoryId) !== String(territoryId)) return;
                if (targetHierarchy && !targetHierarchy.every((name, i) => plainRow.hierarchy[i] === name)) return;
                if (!months.some(m => plainRow[m] !== (plainRow[`ref_${m}`] || 0))) return;

                const row = state.budgetData[index];
                const id = String(plainRow.id);
                rowsBeforeCopy.push(snapshotRow(plainRow));
                const before = captureRowValues(plainRow);

                months.forEach(m => {
                    const refVal = plainRow[`ref_${m}`] || 0;
                    if (plainRow[m] === refVal) return;
                    row[m] = refVal;
                    addCellHistory(state, id, m, plainRow[m], refVal, 'System (Auto-fill)');
                });
                row.total = plainRow.ref_total || 0;
                changes.push({ row, before });

                if (!state.dirtyRowIds.map(String).includes(id)) state.dirtyRowIds.push(id);
            });

            if (changes.length === 0) return;

            const changedIds = new Set(changes.map(c => String(c.row.id)));
            state.invalidRowIds = state.invalidRowIds.filter(id => !changedIds.has(String(id)));
            state.editHistory.push({ rows: rowsBeforeCopy });
            if (state.editHistory.length > MAX_EDIT_HISTORY) state.editHistory.shift();
            state.isDirty = true;
            if (!state.dirtyRowIds.includes(String(territoryId))) {
                state.dirtyRowIds.push(String(territoryId));
            }
            syncParentTotals(state, changes);
        },
        updateBudgetCell: (state, action) => {
            const { nodeId, month, value } = action.payload;
            const rowIndex = findRowIndex(state, nodeId);
            if (rowIndex === -1) return;

            const row = state.budgetData[rowIndex];
            const oldValue = row[month];
            const numValue = Number(value) || 0;
            if (oldValue === numValue) return;

            pushEditHistory(state, [row]);
            const before = captureRowValues(row);
            row[month] = numValue;
            row.total = months.reduce((sum, m) => sum + (row[m] || 0), 0);
            markRowDirty(state, row);
            addCellHistory(state, nodeId, month, oldValue, numValue, 'Me');
            syncParentTotals(state, [{ row, before }]);
        },
        distributeTotal: (state, action) => {
            const { nodeId, newValue } = action.payload;
            const rowIndex = findRowIndex(state, nodeId);
            if (rowIndex === -1) return;

            const row = state.budgetData[rowIndex];
            const totalVal = Number(newValue) || 0;
            const monthlyVal = Number((totalVal / 12).toFixed(4));

            pushEditHistory(state, [row]);
            const before = captureRowValues(row);
            markRowDirty(state, row);

            months.forEach(m => {
                const oldValue = row[m];
                if (oldValue === monthlyVal) return;
                row[m] = monthlyVal;
                addCellHistory(state, nodeId, m, oldValue, monthlyVal, 'Me (Total Distribution)');
            });

            row.total = totalVal;
            syncParentTotals(state, [{ row, before }]);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBudgetData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBudgetData.fulfilled, (state, action) => {
                state.loading = false;
                state.isInitialized = true;
                state.isDirty = false;
                state.dirtyRowIds = [];
                state.invalidRowIds = [];
                state.editHistory = [];
                state.budgetData = action.payload.data || [];
                state.budgetDocumentIds = action.payload.apiDocumentIds || {};
                state.territoryStatuses = action.payload.apiTerritoryStatuses || {};
                state.territoryComments = action.payload.apiTerritoryComments || {};
                state.hiddenBudgetLines = action.payload.hiddenBudgetLines || [];

                state.budgetData.forEach(row => {
                    if (row.type === 'TERRITORY') {
                        row.status = state.territoryStatuses[row.id] || STATUSES.NEW;
                    }
                });
            })
            .addCase(fetchBudgetData.rejected, (state, action) => {
                state.loading = false;
                state.isInitialized = true;
                state.error = action.payload;
            })
            .addCase(saveBudgetData.pending, (state) => {
                if (state.invalidRowIds.length > 0) state.invalidRowIds = [];
            })
            .addCase(saveBudgetData.rejected, (state, action) => {
                const payload = action.payload;
                if (payload && typeof payload === 'object' && Array.isArray(payload.invalidRowIds)) {
                    state.invalidRowIds = payload.invalidRowIds;
                }
            })
            .addCase(saveBudgetData.fulfilled, (state, action) => {
                const savedBudgets = action.payload || [];
                const savedTerritoryIds = new Set(
                    (Array.isArray(savedBudgets) ? savedBudgets : [])
                        .map(tBudget => tBudget.budgetDetails?.territoryId)
                        .filter(id => id !== undefined && id !== null)
                        .map(String)
                );
                if (savedTerritoryIds.size === 0 && state.activeTerritoryId !== null && state.activeTerritoryId !== undefined) {
                    savedTerritoryIds.add(String(state.activeTerritoryId));
                }
                const rows = original(state.budgetData);
                const savedRowIds = new Set(
                    rows
                        .filter(row => savedTerritoryIds.has(String(row.territoryId)))
                        .map(row => String(row.id))
                );
                const dirtyBeforeSave = new Set(state.dirtyRowIds.map(String));
                rows.forEach((plainRow, index) => {
                    const id = String(plainRow.id);
                    if (plainRow.type !== 'PRODUCT' || !savedRowIds.has(id) || !dirtyBeforeSave.has(id)) return;
                    const row = state.budgetData[index];
                    months.forEach(m => { row[`base_${m}`] = Number(plainRow[m]) || 0; });
                });
                state.dirtyRowIds = state.dirtyRowIds.filter(id => !savedTerritoryIds.has(String(id)) && !savedRowIds.has(String(id)));
                state.editHistory = (state.editHistory || []).filter(entry => !entry.rows.some(r => savedRowIds.has(r.id)));
                state.isDirty = state.dirtyRowIds.length > 0;
                if (state.invalidRowIds.length > 0) state.invalidRowIds = [];
                let newDocumentIds = {};
                let newSavedValuesMap = {};

                if (Array.isArray(savedBudgets)) {
                    savedBudgets.forEach(tBudget => {
                        const tId = String(tBudget.budgetDetails?.territoryId);
                        if (tBudget.budgetDetails?.id) {
                            newDocumentIds[tId] = tBudget.budgetDetails.id;
                        }
                        if (tBudget.budgets && Array.isArray(tBudget.budgets)) {
                            tBudget.budgets.forEach(b => {
                                const prodKey = `${tId}-${b.customerNum}-${b.showroomNum || 'null'}-${b.orderCategory}-${b.productNum}`;
                                if (!newSavedValuesMap[prodKey]) newSavedValuesMap[prodKey] = {};

                                if (b.month) {
                                    const shortMonth = b.month.substring(0, 3).toLowerCase();
                                    newSavedValuesMap[prodKey][`${shortMonth}_id`] = b.id;
                                }
                            });
                        }
                    });
                }

                state.budgetDocumentIds = { ...state.budgetDocumentIds, ...newDocumentIds };

                rows.forEach((plainRow, index) => {
                    if (plainRow.type !== 'PRODUCT') return;
                    const prodKey = `${plainRow.territoryId}-${plainRow.companyId}-${plainRow.showroomId || 'null'}-${plainRow.categoryId}-${plainRow.productId}`;
                    const vals = newSavedValuesMap[prodKey];
                    if (!vals) return;
                    const row = state.budgetData[index];
                    row.hasApiData = true;
                    months.forEach(m => {
                        if (vals[`${m}_id`]) row[`${m}_id`] = vals[`${m}_id`];
                    });
                });
            })
            .addCase(changeBudgetStatus.pending, (state) => {
                if (state.invalidRowIds.length > 0) state.invalidRowIds = [];
            })
            .addCase(changeBudgetStatus.rejected, (state, action) => {
                const payload = action.payload;
                if (payload && typeof payload === 'object' && Array.isArray(payload.invalidRowIds)) {
                    state.invalidRowIds = payload.invalidRowIds;
                }
            })
            .addCase(changeBudgetStatus.fulfilled, (state, action) => {
                const { territoryId, newStatus, comment } = action.payload;
                state.territoryStatuses[territoryId] = newStatus;
                if (newStatus === STATUSES.REJECTED) {
                    state.territoryComments[territoryId] = comment || '';
                }
                const rowIndex = findRowIndex(state, territoryId);
                if (rowIndex !== -1) {
                    state.budgetData[rowIndex].status = newStatus;
                }
            })
            .addCase(fetchRowHistory.fulfilled, (state, action) => {
                const { rowId, newData } = action.payload;
                if (!state.historyData[rowId]) {
                    state.historyData[rowId] = {};
                }
                state.historyData[rowId] = { ...state.historyData[rowId], ...newData };
            })
            .addCase(fetchVersionHistory.pending, (state) => {
                state.versionLoading = true;
            })
            .addCase(fetchVersionHistory.fulfilled, (state, action) => {
                state.versionLoading = false;
                state.versions = action.payload || [];
            })
            .addCase(fetchVersionHistory.rejected, (state, action) => {
                state.versionLoading = false;
                state.versions = [];
            })
            .addCase(restoreBudgetVersion.fulfilled, (state, action) => {
                const { territoryId, restoredRows } = action.payload;
                const restoredIds = Object.keys(restoredRows || {});
                if (restoredIds.length === 0) return;

                const rows = original(state.budgetData);
                const restoredIdSet = new Set(restoredIds);
                pushEditHistory(state, rows.filter(row => restoredIdSet.has(String(row.id))));

                const changes = [];
                rows.forEach((plainRow, index) => {
                    const newValues = restoredRows[String(plainRow.id)];
                    if (!newValues) return;
                    const row = state.budgetData[index];
                    const before = captureRowValues(plainRow);
                    months.forEach(m => { row[m] = newValues[m]; });
                    row.total = months.reduce((sum, m) => sum + (Number(row[m]) || 0), 0);
                    changes.push({ row, before });
                    if (!state.dirtyRowIds.includes(String(plainRow.id))) state.dirtyRowIds.push(String(plainRow.id));
                });

                if (!state.dirtyRowIds.includes(String(territoryId))) state.dirtyRowIds.push(String(territoryId));
                state.isDirty = true;
                state.invalidRowIds = [];
                syncParentTotals(state, changes);
            })
            .addCase(fetchEurostatData.fulfilled, (state, action) => {
                state.eurostatData[action.payload.year] = action.payload.data;
            });
    }
});

export const {
    setYear,
    setBudgetType,
    setActiveTerritory,
    updateBudgetCell,
    distributeTotal,
    toggleVersionHistory,
    toggleTooltip,
    toggleEco,
    toggleEditMode,
    setDisplayedRowCount,
    copyFromReference,
    setSelectedRow,
    requestFocusRow,
    clearInvalidRowIds,
    setFilteredRowIds,
    undoLastEdit,
} = budgetSlice.actions;

export default budgetSlice.reducer;